import { ErrorCodeEnum } from "../graphql/types";
import {
  ApolloCallerErrorContext,
  CALLER_HANDLED_USER_ERROR_CONTEXT,
  getPreserveExpectedErrorsForCallerContext,
  PRESERVE_ERROR_FOR_CALLER_CONTEXT,
} from "./apolloCallerContext";

type GraphQLErrorLike = {
  message?: string | null;
  extensions?: {
    code?: unknown;
  } | null;
};

export {
  CALLER_HANDLED_USER_ERROR_CONTEXT,
  getPreserveExpectedErrorsForCallerContext,
  PRESERVE_ERROR_FOR_CALLER_CONTEXT,
};

/**
 * Caller-handled business errors should stay on the current screen without
 * becoming Sentry noise. Network failures and unrecognized GraphQL failures
 * remain observable while the caller still renders a local error state.
 */
export const shouldReportApolloError = ({
  context,
  graphQLErrors,
  hasNetworkError,
}: {
  context?: ApolloCallerErrorContext | null;
  graphQLErrors?: ReadonlyArray<GraphQLErrorLike>;
  hasNetworkError: boolean;
}): boolean => {
  if (context?.preserveErrorForCaller !== true) {
    return true;
  }

  if (context.reportUnexpectedPreservedErrors !== true) {
    return false;
  }

  if (hasNetworkError) {
    return true;
  }

  const errors = graphQLErrors || [];
  if (!errors.length) {
    return true;
  }

  const expectedCodes = new Set(context.expectedGraphQLErrorCodes || []);
  return errors.some((error) => {
    const code = error.extensions?.code;
    return typeof code !== "string" || !expectedCodes.has(code);
  });
};

type PageLifecycleTarget = Pick<Window, "addEventListener">;

export type PageUnloadTracker = {
  isPageUnloading: () => boolean;
};

/**
 * Tracks whether the page is being unloaded (reload, close, or full
 * navigation). Only pagehide sets the flag: beforeunload can be cancelled by
 * the unsaved-changes prompt, and the page then stays open. A page restored
 * from the back/forward cache fires pageshow, so the flag clears again.
 */
export const createPageUnloadTracker = (
  target: PageLifecycleTarget
): PageUnloadTracker => {
  let pageIsUnloading = false;

  target.addEventListener("pagehide", () => {
    pageIsUnloading = true;
  });
  target.addEventListener("pageshow", () => {
    pageIsUnloading = false;
  });

  return { isPageUnloading: () => pageIsUnloading };
};

type NetworkErrorLike = {
  name?: string;
  statusCode?: unknown;
};

/**
 * The browser cancels in-flight requests when the page unloads. The request
 * then rejects with a fetch error ("Failed to fetch", "Load failed") or an
 * AbortError, without an HTTP response. These are not API failures, so they
 * must not become Sentry noise. A network error on a page that stays open,
 * or any error with an HTTP status, is still reported.
 */
export const isNetworkErrorFromPageUnload = ({
  networkError,
  pageIsUnloading,
}: {
  networkError?: NetworkErrorLike | null;
  pageIsUnloading: boolean;
}): boolean => {
  if (!networkError || typeof networkError.statusCode === "number") {
    return false;
  }

  return pageIsUnloading || networkError.name === "AbortError";
};

const SESSION_REVOCATION_ERROR_CODES = new Set<string>([
  ErrorCodeEnum.DisallowAccessTokenWithUserAuthYoungerThanUtcMilli,
  ErrorCodeEnum.DisallowAccessTokenPermanently,
]);

/**
 * Only backend codes that explicitly revoke or invalidate the current access
 * token should sign the user out. Resource authorization failures and
 * user-type permission failures belong to the calling screen and must not
 * destroy an otherwise valid Chamber Pro session.
 */
export const shouldLogoutForGraphQLErrors = (
  graphQLErrors?: ReadonlyArray<GraphQLErrorLike>
): boolean =>
  (graphQLErrors || []).some((error) => {
    const extensionCode = error.extensions?.code;

    return (
      (typeof extensionCode === "string" &&
        SESSION_REVOCATION_ERROR_CODES.has(extensionCode)) ||
      SESSION_REVOCATION_ERROR_CODES.has((error.message || "").trim())
    );
  });
